import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getAllRfidMaster } from "../../../Apis/rfidApi";
import {
  DeleteRouteStoppage,
  GetRouteStoppages,
} from "../../../Apis/routeMasterApi";
import { useRouteMasterStore } from "./RouteMasterStore/useRouteMasterStore";
import {
  createEmptyStoppage,
  parseRouteCoordinatesFromDetail,
  parseRouteStoppagesResponse,
  parseStoppagesFromDetail,
} from "./routeMasterFormUtils";
import { parseAssignedVehicles } from "./routeVehicleModalUtils";

const extractRfidVehicleList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.rfidMasters)) return response.rfidMasters;
  return [];
};

const mapRfidVehicles = (list = []) =>
  list
    .filter((item) => item?.isActive !== false)
    .map((item) => {
      const otherNo = item.otherNo?.toString().trim() || "";
      const vehicleId =
        item.vehicleId ??
        item.rfidMasterId ??
        item.assetRfidId ??
        item.rfidId ??
        "";

      return {
        ...item,
        vehicleId,
        otherNo,
        vehicleNumber: otherNo,
        rfidMasterId: item.rfidMasterId ?? item.assetRfidId ?? item.rfidId,
      };
    })
    .filter((item) => item.vehicleId !== "" && item.otherNo);

export const useRouteVehicleModalData = ({ showModal, editRow }) => {
  const { fetchRouteDetailById } = useRouteMasterStore();
  const [vehicles, setVehicles] = useState([]);
  const [stoppages, setStoppages] = useState([]);
  const [routeDetail, setRouteDetail] = useState(null);
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const [isLoadingStoppages, setIsLoadingStoppages] = useState(false);
  const [activeStoppageIndex, setActiveStoppageIndex] = useState(null);

  const routeId = editRow?.routeId ?? routeDetail?.routeId;

  const fetchRouteStoppages = useCallback(async (id) => {
    if (!id) {
      setStoppages([]);
      return [];
    }

    setIsLoadingStoppages(true);
    try {
      const response = await GetRouteStoppages(id);
      const mappedStoppages = parseRouteStoppagesResponse(response);
      setStoppages(mappedStoppages);
      return mappedStoppages;
    } catch {
      setStoppages([]);
      toast.error("Unable to load route stoppages.");
      return [];
    } finally {
      setIsLoadingStoppages(false);
    }
  }, []);

  useEffect(() => {
    if (!showModal) {
      setRouteDetail(null);
      setStoppages([]);
      setActiveStoppageIndex(null);
      return;
    }

    const loadModalData = async () => {
      setIsLoadingRoute(true);

      try {
        const response = await getAllRfidMaster({
          pageNumber: 1,
          pageSize: 1000,
        });
        setVehicles(mapRfidVehicles(extractRfidVehicleList(response)));
      } catch {
        setVehicles([]);
        toast.error("Unable to load vehicle list.");
      }

      let detail = editRow;
      if (editRow?.routeId) {
        const fetchedDetail = await fetchRouteDetailById(editRow.routeId);
        if (fetchedDetail) {
          detail = { ...editRow, ...fetchedDetail };
        }
      }

      setRouteDetail(detail);

      if (detail?.routeId) {
        await fetchRouteStoppages(detail.routeId);
      } else {
        setStoppages(parseStoppagesFromDetail(detail));
      }

      setActiveStoppageIndex(null);
      setIsLoadingRoute(false);
    };

    loadModalData();
  }, [showModal, editRow, fetchRouteDetailById, fetchRouteStoppages]);

  const routePoints = useMemo(() => {
    const detail = routeDetail ?? editRow ?? {};
    if (
      Array.isArray(detail.routeCoordinates) &&
      detail.routeCoordinates.length
    ) {
      const points = detail.routeCoordinates
        .map((point) => {
          const lat = Number(point?.lat ?? point?.latitude);
          const lng = Number(point?.lng ?? point?.longitude);
          if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
          return { lat, lng };
        })
        .filter(Boolean);
      if (points.length) return points;
    }
    return parseRouteCoordinatesFromDetail(detail);
  }, [routeDetail, editRow]);

  const assignedVehicles = useMemo(
    () => parseAssignedVehicles(routeDetail ?? editRow ?? {}),
    [routeDetail, editRow]
  );

  const openStoppageMap = (index) => {
    if (isLoadingRoute || isLoadingStoppages) return;
    setActiveStoppageIndex(index);
  };

  const handleAddStoppage = () => {
    if (isLoadingRoute || isLoadingStoppages) return;
    // Open map in "new" mode (-1). Do not add a row until Save succeeds.
    setActiveStoppageIndex(-1);
  };

  const handleCloseStoppageMap = () => {
    setActiveStoppageIndex(null);
  };

  const handleDeleteStoppage = async (index) => {
    const stoppage = stoppages[index];
    if (!stoppage) return;

    const stoppageId = Number(stoppage.stoppageId);

    if (!stoppageId) {
      setStoppages((previous) =>
        previous
          .filter((_, itemIndex) => itemIndex !== index)
          .map((item, itemIndex) => ({
            ...item,
            sequence: itemIndex + 1,
          }))
      );
      return;
    }

    setIsLoadingStoppages(true);
    try {
      const response = await DeleteRouteStoppage(stoppageId);
      const responseData = response?.data ?? response;
      const isSuccess =
        responseData?.success === true ||
        responseData?.success === "true" ||
        response?.status === 200;

      if (!isSuccess && responseData?.success === false) {
        throw new Error(
          responseData?.message || "Unable to delete route stoppage."
        );
      }

      toast.success(responseData?.message || "Stoppage deleted successfully.");

      if (routeId) {
        await fetchRouteStoppages(routeId);
      } else {
        setStoppages((previous) =>
          previous
            .filter((_, itemIndex) => itemIndex !== index)
            .map((item, itemIndex) => ({
              ...item,
              sequence: itemIndex + 1,
            }))
        );
      }
    } catch (error) {
      const data = error?.response?.data;
      const message =
        (typeof data === "string" && data) ||
        data?.message ||
        error?.message ||
        "Unable to delete route stoppage.";
      toast.error(message);
    } finally {
      setIsLoadingStoppages(false);
    }
  };

  const handleSaveStoppage = async (savedStoppage) => {
    if (routeId) {
      await fetchRouteStoppages(routeId);
      return;
    }

    // Fallback only if routeId is missing (should be rare in assign/reassign)
    if (activeStoppageIndex === -1) {
      setStoppages((previous) => [
        ...previous,
        {
          ...createEmptyStoppage(previous.length + 1),
          ...savedStoppage,
          sequence: previous.length + 1,
        },
      ]);
      return;
    }

    if (activeStoppageIndex === null) return;

    setStoppages((previous) =>
      previous.map((item, itemIndex) =>
        itemIndex === activeStoppageIndex
          ? { ...item, ...savedStoppage, sequence: item.sequence ?? itemIndex + 1 }
          : item
      )
    );
  };

  const getRouteMeta = () => ({
    routeId: routeDetail?.routeId ?? editRow?.routeId,
    routeCode: routeDetail?.routeCode ?? editRow?.routeCode,
    routeDescription: routeDetail?.routeDescription ?? editRow?.routeDescription,
  });

  return {
    vehicles,
    stoppages,
    routeDetail,
    isLoadingRoute: isLoadingRoute || isLoadingStoppages,
    activeStoppageIndex,
    setActiveStoppageIndex,
    routePoints,
    assignedVehicles,
    openStoppageMap,
    handleAddStoppage,
    handleCloseStoppageMap,
    handleDeleteStoppage,
    handleSaveStoppage,
    fetchRouteStoppages,
    getRouteMeta,
  };
};
